import type { Nivel } from '@/api/alianca';
import { Button } from '@/components/ui/Button';
import { Eyebrow } from '@/components/ui/Typography';
import { TierPill } from '@/components/alianca/AliancaPrimitives';
import { tierByKey } from '@/lib/alianca-tiers';

interface AliancaRiscoProps {
  fromNivel: Nivel;
  toNivel: Nivel;
  /**
   * kWh aproximados pra recuperar o nível anterior — opcional pois o backend
   * MVP ainda não retorna esse cálculo. Default = (from.min - to.min + 1)
   * (cliente precisa cruzar a fronteira de novo).
   */
  kwhPraRecuperar?: number;
  onFindStation: () => void;
  onDismiss: () => void;
}

/**
 * Overlay full-screen · queda de nível (direction='down') ou risco iminente.
 *
 * Tom honesto, não alarmista. Mostra:
 *  - Card do nível atual (queda já aconteceu — fromNivel viraram histórico).
 *  - Diferença de cashback em % (visualmente: from.pct → to.pct).
 *  - Caminho de recuperação: "Carregar ~N kWh em uma recarga média".
 *  - CTA "Encontrar carregador perto".
 *
 * Design ref: design/redesign/bundle/app-bow-recarga/project/alianca-variants.jsx
 * (AliancaRisco — janela 90d, tom honesto).
 *
 * NB: o design original tem uma timeline de "próximas saídas da janela" que
 * requer um endpoint adicional pra cálculo preditivo (V2). Por ora, este
 * overlay dispara após a queda já registrada — recuperação é o foco.
 */
export function AliancaRisco({
  fromNivel,
  toNivel,
  kwhPraRecuperar,
  onFindStation,
  onDismiss
}: AliancaRiscoProps) {
  const from = tierByKey(fromNivel); // nível anterior (perdido)
  const to = tierByKey(toNivel); // nível atual (caiu pra cá)
  const kwhDelta = kwhPraRecuperar ?? Math.max(35, from.min - to.min + 1);

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col overflow-y-auto"
      style={{ background: '#F1EDE4' }}
      role="dialog"
      aria-modal="true"
    >
      {/* Header simples */}
      <div
        className="flex items-center justify-between px-5 pb-3 pt-4"
        style={{ paddingTop: 'max(1rem, env(safe-area-inset-top))' }}
      >
        <Eyebrow>Programa Aliança</Eyebrow>
        <button
          type="button"
          onClick={onDismiss}
          className="inline-flex h-9 w-9 items-center justify-center rounded-full border-0 text-bow-royal"
          style={{ background: 'rgba(0,27,116,0.06)' }}
          aria-label="Fechar"
        >
          ✕
        </button>
      </div>

      <div className="flex-1 px-5 pb-8">
        {/* Status card — nível atual com barra lateral */}
        <div className="relative overflow-hidden rounded-[22px] bg-white p-5 shadow-soft">
          <div
            className="absolute bottom-0 left-0 top-0 w-1.5"
            style={{ background: to.hex }}
          />
          <div className="pl-3">
            <Eyebrow>Nível atual</Eyebrow>
            <div
              className="mt-1 text-bow-royal"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 30,
                letterSpacing: '-0.025em',
                lineHeight: 1
              }}
            >
              {to.label}{' '}
              <span className="text-sm font-medium text-bow-royal/55">
                · {to.pct}%
              </span>
            </div>
          </div>
        </div>

        {/* Card principal — tom honesto */}
        <div
          className="relative mt-3.5 overflow-hidden rounded-[22px] bg-white p-5"
          style={{ border: `2px solid ${from.hex}` }}
        >
          <div
            className="absolute -right-5 -top-5 h-24 w-24 rounded-full"
            style={{ background: `${from.hex}1A` }}
          />
          <div className="relative">
            <span
              className="inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-[0.02em]"
              style={{ background: `${from.hex}15`, color: from.hex }}
            >
              <svg
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.4"
                strokeLinecap="round"
                aria-hidden
              >
                <circle cx="12" cy="12" r="10" />
                <path d="M12 8v4M12 16v.01" />
              </svg>
              JANELA DE 90 DIAS
            </span>
            <div
              className="mt-3.5 text-bow-royal"
              style={{
                fontFamily: 'Chillax, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 26,
                letterSpacing: '-0.025em',
                lineHeight: 1.2
              }}
            >
              Recargas antigas saíram da sua janela.
            </div>
            <div className="mt-2 text-[13px] leading-relaxed text-bow-royal/70">
              Você passou de <TierPill tier={from} size="sm" /> para{' '}
              <TierPill tier={to} size="sm" />. O cashback nas próximas recargas
              ajusta para o novo nível.
            </div>

            {/* Diferença numérica */}
            <div
              className="mt-4 flex items-center gap-3 rounded-2xl px-3.5 py-3"
              style={{ background: 'rgba(0,27,116,0.04)' }}
            >
              <div className="flex-1">
                <div className="text-[11px] font-medium text-bow-royal/55">
                  Diferença de cashback
                </div>
                <div
                  className="font-semibold text-bow-royal"
                  style={{ fontSize: 22, letterSpacing: '-0.02em' }}
                >
                  <span style={{ color: from.hex }}>{from.pct}%</span> →{' '}
                  <span style={{ color: to.hex }}>{to.pct}%</span>
                </div>
              </div>
              <div
                className="rounded-full px-2.5 py-1.5 text-[11px] font-semibold text-bow-royal"
                style={{ background: '#F1EDE4' }}
              >
                −{from.pct - to.pct} pp
              </div>
            </div>

            {/* Caminho de recuperação */}
            <div
              className="mt-4 rounded-[18px] p-4 text-white"
              style={{ background: from.hex }}
            >
              <div className="text-[11px] font-semibold uppercase tracking-[0.06em] opacity-85">
                Como voltar pra {from.label}
              </div>
              <div
                className="mt-1.5"
                style={{
                  fontFamily: 'Chillax, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: 28,
                  letterSpacing: '-0.025em',
                  lineHeight: 1
                }}
              >
                Carregar {kwhDelta} kWh
              </div>
              <div className="mt-1.5 text-xs opacity-90">
                ≈ 1 recarga média. Cada kWh conta na janela de 90 dias.
              </div>
              <Button
                variant="cream"
                size="md"
                fullWidth
                className="mt-3.5"
                onClick={onFindStation}
              >
                Encontrar carregador perto
              </Button>
            </div>
          </div>
        </div>

        {/* Ação secundária */}
        <button
          type="button"
          onClick={onDismiss}
          className="mt-4 h-11 w-full rounded-full border-0 bg-transparent text-sm font-medium text-bow-royal/65"
        >
          Entendi, fechar
        </button>
      </div>
    </div>
  );
}
